import type {ReactNode} from 'react';

import styled from '@emotion/styled';

import {LegalFooter} from 'UI/components/layout/LegalFooter';

import {useAppLayoutChrome} from './useAppLayoutChrome';

import type {LegalPage} from 'types/payload-types';

/**
 * The props for the `AppLayout` component.
 * Configures the app shell content area between the persistent header and bottom navigation.
 *
 * `activeTab`, `isBackVisible`, `onBack`, and `realBackLink` are not consumed by this component directly -
 * `_app.tsx` reads them off this element's props to drive the persistent `AppHeader`/`NavigationBar`, which
 * stay mounted across route changes instead of fading out/in with the page content.
 */
interface ComponentProps {
    /** The key of the currently active navigation tab. */
    activeTab: string;
    /** The main page content rendered between header and navigation bar. */
    children: ReactNode;
    /** Whether to hide the bottom navigation bar (e.g. When a player is shown). */
    hideNavBar?: boolean;
    /** Whether to show the back arrow in the header. */
    isBackVisible?: boolean;
    /**
     * The legal pages (Impressum, Datenschutz, etc.), loaded via `getLegalPages` in the page's
     * `getStaticProps`/`getServerSideProps` and rendered as the `LegalFooter`. Omitted on pages
     * with `hideNavBar` (e.g. The walk player), where the footer is hidden anyway..
     *
     * @default []
     */
    legalPages?: LegalPage[];
    /** Callback when the header back button is pressed. Defaults to `router.back()`. */
    onBack?(): void;
    realBackLink?: boolean;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'AppLayout'
     */
    testId?: string;
}

/**
 * The app shell content area, rendered between the persistent `AppHeader` and `NavigationBar`
 * that `_app.tsx` renders outside the page-transition animation. Handles the bottom padding
 * to prevent content from being obscured by the fixed navigation.
 *
 * @param props            The component props.
 * @param props.children   The main page content.
 * @param props.hideNavBar Whether to hide the bottom navigation bar. Defaults to `false`.
 * @param props.legalPages The legal pages rendered as the `LegalFooter`. Defaults to `[]`.
 * @param props.testId     An optional test ID used for automation selectors. Defaults to `'AppLayout'`.
 * @returns A JSX element rendering the AppLayout.
 *
 * @example
 * ```tsx
 * <AppLayout activeTab="home">
 *     <HomePageContent />
 * </AppLayout>
 * ```
 */
const AppLayout = ({
    children,
    hideNavBar = false,
    legalPages = [],
    testId = 'AppLayout',
    ...chromeProps
}: ComponentProps) => {
    useAppLayoutChrome(chromeProps);

    return (
        <Page $hideNavBar={hideNavBar} data-cy={testId}>
            <Main>
                {children}
                {!hideNavBar && <LegalFooter legalPages={legalPages} />}
            </Main>
        </Page>
    );
};

AppLayout.displayName = 'AppLayout';

export {AppLayout};
export type {ComponentProps as AppLayoutProps};

// ── Styled ──────────────────────────────────────────────────────────────

/** Props for the page wrapper. */
interface PageProps {
    /** Whether the bottom navigation bar is hidden. */
    $hideNavBar: boolean;
}

const Page = styled.div<PageProps>`
    background: ${({theme}) => theme.colors.surfaceBackground};
    min-height: 100dvh;
    padding-bottom: ${({$hideNavBar}) => ($hideNavBar ? '0' : '6.4rem')};
`;

const Main = styled.main`
    display: flex;
    flex-direction: column;
`;